import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { useDebounce } from "../../hooks/useDebounce";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useCabangOptions } from "../../lib/cabang";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta } from "../../components/ui/responsive-table";
import { hariIniISO, formatTanggal } from "../../lib/tanggal";
import InputRupiah from "../../components/InputRupiah";
import {
  Plus, FileStack, Search, SlidersHorizontal, ArrowUpDown, X, Settings2,
  LayoutDashboard, ListFilter, TrendingUp, AlertTriangle, Wallet, CheckCircle2,
  RotateCcw, ChevronRight, Tag, Trash2, RefreshCw,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

// KENAPA konstanta path terpisah: router `credit_note.py` di backend
// TIDAK punya prefix "/finance" pada literal path-nya (`@router.post("/credit-note")`,
// dst — pola yang SAMA dengan `routers/retur.py`, BUKAN pola `routers/finance/*`
// yang bersandar pada prefix router agregator). Router ini sengaja BELUM
// di-mount ke server.py saat berkas ini ditulis (lihat
// `backend/tests/test_credit_note.py`) — pemasangannya di tangan owner.
// Kalau ternyata di-mount di bawah "/finance", cukup ubah SATU baris ini.
const CN_BASE = "/credit-note";

const STATUS_META = {
  draft:              { label: "Draft",              cls: "bg-slate-100 text-slate-600 border-slate-200" },
  dokumen_dibuat:     { label: "Dokumen Dibuat",      cls: "bg-sky-50 text-sky-700 border-sky-200" },
  dokumen_diserahkan: { label: "Diserahkan",          cls: "bg-sky-50 text-sky-700 border-sky-200" },
  menunggu_supplier:  { label: "Menunggu Supplier",   cls: "bg-amber-50 text-amber-700 border-amber-200" },
  siap_cair:          { label: "Siap Cair",           cls: "bg-orange-50 text-orange-700 border-orange-200" },
  partially_used:     { label: "Sebagian Terpakai",   cls: "bg-amber-100 text-amber-800 border-amber-300" },
  cair:               { label: "Cair",                cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  terpakai:           { label: "Terpakai",            cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed:             { label: "Closed",              cls: "bg-slate-800 text-white border-slate-800" },
  dibatalkan:         { label: "Dibatalkan",          cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

// Warna per bucket aging — meniru MAKNA warna backend (`AGING_BUCKETS` di
// `credit_note.py`), diterjemahkan ke kelas Tailwind slate/emerald/amber/
// orange/rose yang sudah dipakai di halaman Finance lain (bukan palet baru).
export const AGING_CLS = {
  belum_diserahkan: "bg-slate-100 text-slate-600 border-slate-200",
  normal:           "bg-emerald-50 text-emerald-700 border-emerald-200",
  monitoring:       "bg-amber-50 text-amber-700 border-amber-200",
  warning:          "bg-orange-50 text-orange-700 border-orange-200",
  high_risk:        "bg-rose-100 text-rose-700 border-rose-300",
  critical:         "bg-rose-900 text-white border-rose-900",
};

export function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

export function AgingPill({ aging, overdue }) {
  if (!aging || aging.hari == null) {
    return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium ${AGING_CLS.belum_diserahkan}`}>{aging?.label || "Belum Diserahkan"}</span>;
  }
  const cls = AGING_CLS[aging.bucket] || AGING_CLS.belum_diserahkan;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${cls}`}>{aging.label}</span>
      {overdue && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-600 text-white font-bold whitespace-nowrap">OVERDUE</span>}
    </span>
  );
}

const STATUS_OPTIONS = [
  { value: "", label: "Semua Status" },
  { value: "draft", label: "Draft" },
  { value: "dokumen_dibuat", label: "Dokumen Dibuat" },
  { value: "menunggu_supplier", label: "Menunggu Supplier" },
  { value: "siap_cair", label: "Siap Cair" },
  { value: "partially_used", label: "Sebagian Terpakai" },
  { value: "cair", label: "Cair" },
  { value: "terpakai", label: "Terpakai" },
  { value: "closed", label: "Closed" },
  { value: "dibatalkan", label: "Dibatalkan" },
];

const AGING_OPTIONS = [
  { value: "", label: "Semua Aging" },
  { value: "belum_diserahkan", label: "Belum Diserahkan" },
  { value: "normal", label: "Normal (0-30 hari)" },
  { value: "monitoring", label: "Monitoring (31-60 hari)" },
  { value: "warning", label: "Warning (61-90 hari)" },
  { value: "high_risk", label: "High Risk (91-120 hari)" },
  { value: "critical", label: "Critical (>120 hari)" },
];

const SORT_OPTIONS = [
  { value: "created_at", label: "Terbaru Dibuat" },
  { value: "supplier_nama", label: "Nama Perusahaan (Supplier)" },
  { value: "brand", label: "Brand" },
  { value: "nilai", label: "Nilai CN" },
  { value: "outstanding", label: "Outstanding" },
  { value: "aging", label: "Aging (Hari)" },
  { value: "expected_settlement_date", label: "Expected Settlement" },
  { value: "tanggal_diserahkan", label: "Tanggal Diserahkan" },
  { value: "tanggal_terbit", label: "Tanggal Terbit" },
];

const EMPTY_FORM = {
  supplier_nama: "", brand: "", cabang: "", kategori: "", nilai: "",
  tanggal_terbit: hariIniISO(), no_referensi: "", keterangan: "", expected_settlement_date: "",
};

export default function FinanceCreditNote() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const cabangOptions = useCabangOptions({ denganKosong: true });

  const [view, setView] = useState("list"); // "list" | "dashboard"

  // ---- Filter state (baca CLAUDE.md: hariIniISO/DatePicker wajib, jangan toISOString) ----
  const [q, setQ] = useState("");
  const [supplier, setSupplier] = useState("");
  const [brand, setBrand] = useState("");
  const [cabang, setCabang] = useState("");
  const [kategoriF, setKategoriF] = useState("");
  const [pic, setPic] = useState("");
  const [statusF, setStatusF] = useState("");
  const [agingBucket, setAgingBucket] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [nominalMin, setNominalMin] = useState("");
  const [nominalMax, setNominalMax] = useState("");
  const [tanggalDari, setTanggalDari] = useState("");
  const [tanggalSampai, setTanggalSampai] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const qDeb = useDebounce(q, 400);
  const picDeb = useDebounce(pic, 400);
  const nominalMinDeb = useDebounce(nominalMin, 500);
  const nominalMaxDeb = useDebounce(nominalMax, 500);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total_nilai: 0, total_outstanding: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);

  const [kategoriOpts, setKategoriOpts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showKategori, setShowKategori] = useState(false);

  const canCreate = can("finance_credit_note", "create");
  const canManageKategori = can("finance_credit_note", "delete");

  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const r = await api.get(`${CN_BASE}/kategori-options`);
        if (!batal) setKategoriOpts(r.data.kategori || []);
      } catch { /* form tetap jalan dengan pilihan kosong */ }
    })();
    return () => { batal = true; };
  }, [reload]);

  useEffect(() => {
    if (view !== "list") return;
    let batal = false;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const params = { sort_by: sortBy, sort_dir: sortDir, limit: 500 };
        if (qDeb) params.q = qDeb;
        if (supplier) params.supplier = supplier;
        if (brand) params.brand = brand;
        if (cabang) params.cabang = cabang;
        if (kategoriF) params.kategori = kategoriF;
        if (picDeb) params.pic = picDeb;
        if (statusF) params.status = statusF;
        if (agingBucket) params.aging_bucket = agingBucket;
        if (overdueOnly) params.overdue = true;
        if (nominalMinDeb) params.nominal_min = Number(nominalMinDeb);
        if (nominalMaxDeb) params.nominal_max = Number(nominalMaxDeb);
        if (tanggalDari) params.tanggal_dari = tanggalDari;
        if (tanggalSampai) params.tanggal_sampai = tanggalSampai;
        const r = await api.get(CN_BASE, { params });
        if (batal) return;
        setItems(r.data.items || []);
        setSummary({ count: r.data.count || 0, total_nilai: r.data.total_nilai || 0, total_outstanding: r.data.total_outstanding || 0 });
      } catch (e) {
        if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat daftar Credit Note"); }
      } finally { if (!batal) setLoading(false); }
    })();
    return () => { batal = true; };
  }, [view, qDeb, supplier, brand, cabang, kategoriF, picDeb, statusF, agingBucket, overdueOnly, nominalMinDeb, nominalMaxDeb, tanggalDari, tanggalSampai, sortBy, sortDir, reload]);

  const resetFilter = () => {
    setQ(""); setSupplier(""); setBrand(""); setCabang(""); setKategoriF(""); setPic("");
    setStatusF(""); setAgingBucket(""); setOverdueOnly(false); setNominalMin(""); setNominalMax("");
    setTanggalDari(""); setTanggalSampai(""); setSortBy("created_at"); setSortDir("desc");
  };

  const latestPic = (it) => {
    const fus = it.follow_ups || [];
    if (!fus.length) return "-";
    return [...fus].sort((a, b) => (a.at || "").localeCompare(b.at || "")).slice(-1)[0].pic || "-";
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-credit-note">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Piutang ke Supplier</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <FileStack className="w-7 h-7 text-emerald-600" /> Credit Note
          </h1>
          <p className="text-sm text-slate-600 mt-1">Rebate, cashback, klaim & insentif supplier yang belum cair/terpakai.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="bg-white border border-slate-200 rounded-lg p-1 inline-flex">
            <button onClick={() => setView("list")} data-testid="cn-view-daftar"
                    className={`h-8 px-3 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <ListFilter className="w-3.5 h-3.5" /> Daftar
            </button>
            <button onClick={() => setView("dashboard")} data-testid="cn-view-dashboard"
                    className={`h-8 px-3 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition ${view === "dashboard" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
            </button>
          </div>
          {canManageKategori && (
            <button onClick={() => setShowKategori(true)} data-testid="cn-kat-manage"
                    className="h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
              <Settings2 className="w-4 h-4" /> Kelola Kategori
            </button>
          )}
          {canCreate && (
            <button onClick={() => setShowForm(true)} data-testid="cn-new"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Credit Note Baru
            </button>
          )}
        </div>
      </div>

      {view === "dashboard" ? (
        <CreditNoteDashboard reloadKey={reload} onNavigateFiltered={(f) => {
          setView("list");
          if (f.overdue) setOverdueOnly(true);
          if (f.supplier) setSupplier(f.supplier);
        }} />
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nomor CN, supplier, atau no. referensi..."
                       data-testid="cn-search" className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none" />
              </div>
              <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="cn-filter-status"
                      className="h-10 px-3 border border-slate-200 rounded-lg text-sm min-w-[170px]">
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={agingBucket} onChange={(e) => setAgingBucket(e.target.value)} data-testid="cn-filter-aging"
                      className="h-10 px-3 border border-slate-200 rounded-lg text-sm min-w-[170px]">
                {AGING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={() => setOverdueOnly((v) => !v)} data-testid="cn-filter-overdue"
                      className={`h-10 px-3 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 ${overdueOnly ? "bg-rose-600 border-rose-600 text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                <AlertTriangle className="w-4 h-4" /> Overdue
              </button>
              <button onClick={() => setShowAdvanced((v) => !v)} data-testid="cn-filter-lanjutan"
                      className={`h-10 px-3 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 ${showAdvanced ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                <SlidersHorizontal className="w-4 h-4" /> Filter Lanjutan
              </button>
              {(q || supplier || brand || cabang || kategoriF || pic || statusF || agingBucket || overdueOnly || nominalMin || nominalMax || tanggalDari || tanggalSampai || sortBy !== "created_at" || sortDir !== "desc") && (
                <button onClick={resetFilter} data-testid="cn-filter-reset"
                        className="h-10 px-3 rounded-lg text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1.5">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset
                </button>
              )}
            </div>

            {showAdvanced && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-slate-100" data-testid="cn-filter-advanced">
                <FilterField label="Supplier">
                  <input value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="cn-filter-supplier" className="input" />
                </FilterField>
                <FilterField label="Brand">
                  <input value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="cn-filter-brand" className="input" />
                </FilterField>
                <FilterField label="Cabang">
                  <select value={cabang} onChange={(e) => setCabang(e.target.value)} data-testid="cn-filter-cabang" className="input">
                    {cabangOptions.map((c) => <option key={c || "_all"} value={c}>{c || "Semua Cabang"}</option>)}
                  </select>
                </FilterField>
                <FilterField label="Kategori">
                  <select value={kategoriF} onChange={(e) => setKategoriF(e.target.value)} data-testid="cn-filter-kategori" className="input">
                    <option value="">Semua Kategori</option>
                    {kategoriOpts.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </FilterField>
                <FilterField label="PIC Follow-up">
                  <input value={pic} onChange={(e) => setPic(e.target.value)} data-testid="cn-filter-pic" className="input" />
                </FilterField>
                <FilterField label="Nominal Min">
                  <InputRupiah value={nominalMin} onChange={setNominalMin} testid="cn-filter-nom-min" className="input" />
                </FilterField>
                <FilterField label="Nominal Max">
                  <InputRupiah value={nominalMax} onChange={setNominalMax} testid="cn-filter-nom-max" className="input" />
                </FilterField>
                <FilterField label="Tanggal Terbit Dari">
                  <DatePicker value={tanggalDari} onChange={setTanggalDari} data-testid="cn-filter-tgl-dari" />
                </FilterField>
                <FilterField label="Tanggal Terbit Sampai">
                  <DatePicker value={tanggalSampai} onChange={setTanggalSampai} data-testid="cn-filter-tgl-sampai" />
                </FilterField>
                <FilterField label="Urutkan">
                  <div className="flex gap-1.5">
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="cn-sort-by" className="input flex-1">
                      {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))} data-testid="cn-sort-dir"
                            title={sortDir === "asc" ? "Naik (A-Z / kecil-besar)" : "Turun (Z-A / besar-kecil)"}
                            className="h-10 w-10 shrink-0 rounded-lg border border-slate-200 hover:bg-slate-50 grid place-items-center text-slate-600">
                      <ArrowUpDown className="w-4 h-4" />
                    </button>
                  </div>
                </FilterField>
              </div>
            )}
            <style>{`.input { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .input:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.1); }`}</style>
          </div>

          <p className="text-sm text-slate-600" data-testid="cn-summary-line">
            {summary.count} Credit Note · Nilai {idr(summary.total_nilai)} · Outstanding <span className="font-semibold text-slate-900">{idr(summary.total_outstanding)}</span>
          </p>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <TabelDesktop>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2.5 text-left">Kode / Terbit</th>
                    <th className="px-3 py-2.5 text-left">Supplier / Brand</th>
                    <th className="px-3 py-2.5 text-left">Kategori</th>
                    <th className="px-3 py-2.5 text-right">Nilai Awal</th>
                    <th className="px-3 py-2.5 text-right">Cair</th>
                    <th className="px-3 py-2.5 text-right">Terpakai</th>
                    <th className="px-3 py-2.5 text-right">Outstanding</th>
                    <th className="px-3 py-2.5 text-left">Diserahkan</th>
                    <th className="px-3 py-2.5 text-left">Exp. Settlement</th>
                    <th className="px-3 py-2.5 text-left">Aging</th>
                    <th className="px-3 py-2.5 text-left">PIC</th>
                    <th className="px-3 py-2.5 text-center">Status</th>
                    <th className="px-3 py-2.5 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  <LoadingState loading={loading} error={loadError} onRetry={bump} kosong={!loading && !loadError && items.length === 0}
                                pesanKosong="Belum ada Credit Note pada filter ini" varian="tabel" kolom={13} data-testid="cn-loading">
                    {items.map((it) => (
                      <tr key={it.id} data-testid={`cn-row-${it.id}`} onClick={() => navigate(`/finance/credit-note/${it.id}`)}
                          className="hover:bg-slate-50/60 cursor-pointer">
                        <td className="px-3 py-3">
                          <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                          <p className="text-slate-800">{formatTanggal(it.tanggal_terbit)}</p>
                        </td>
                        <td className="px-3 py-3">
                          <p className="font-medium text-slate-900">{it.supplier_nama}</p>
                          {it.brand && <p className="text-[10px] text-slate-500">{it.brand}</p>}
                        </td>
                        <td className="px-3 py-3 text-slate-700 text-xs">{it.kategori}</td>
                        <td className="px-3 py-3 text-right font-mono text-slate-800">{idr(it.nilai)}</td>
                        <td className="px-3 py-3 text-right font-mono text-slate-700">{idr(it.nominal_cair)}</td>
                        <td className="px-3 py-3 text-right font-mono text-slate-700">{idr(it.nominal_terpakai)}</td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-slate-900">{idr(it.outstanding)}</td>
                        <td className="px-3 py-3 text-xs text-slate-700">{it.tanggal_diserahkan ? formatTanggal(it.tanggal_diserahkan) : "-"}</td>
                        <td className="px-3 py-3 text-xs text-slate-700">{it.expected_settlement_date ? formatTanggal(it.expected_settlement_date) : "-"}</td>
                        <td className="px-3 py-3"><AgingPill aging={it._aging} overdue={it.overdue} /></td>
                        <td className="px-3 py-3 text-xs text-slate-700">{latestPic(it)}</td>
                        <td className="px-3 py-3 text-center"><StatusPill status={it.status} /></td>
                        <td className="px-3 py-3 text-right">
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/finance/credit-note/${it.id}`); }}
                                  data-testid={`cn-open-${it.id}`} className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-0.5 text-xs font-medium">
                            Detail <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </LoadingState>
                </tbody>
              </table>
            </TabelDesktop>

            <DaftarKartuMobile testid="cn-kartu-mobile">
              <LoadingState loading={loading} error={loadError} onRetry={bump} kosong={!loading && !loadError && items.length === 0}
                            pesanKosong="Belum ada Credit Note pada filter ini" varian="kartu" data-testid="cn-loading-m">
                {items.map((it) => (
                  <KartuBaris key={it.id} testid={`cn-card-${it.id}`} onClick={() => navigate(`/finance/credit-note/${it.id}`)}>
                    <KartuBarisHeader
                      kiri={<>
                        <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                        <p className="text-sm font-semibold text-slate-900">{it.supplier_nama}</p>
                        {it.brand && <p className="text-[10px] text-slate-500">{it.brand}</p>}
                      </>}
                      kanan={<StatusPill status={it.status} />}
                    />
                    <KartuBarisMeta label="Kategori" value={it.kategori} />
                    <KartuBarisMeta label="Nilai Awal" value={idr(it.nilai)} />
                    <KartuBarisMeta label="Outstanding" value={idr(it.outstanding)} tebal />
                    <KartuBarisMeta label="Diserahkan" value={it.tanggal_diserahkan ? formatTanggal(it.tanggal_diserahkan) : "-"} />
                    <div className="flex items-center justify-between pt-1">
                      <AgingPill aging={it._aging} overdue={it.overdue} />
                      <span className="text-[11px] text-emerald-700 font-medium inline-flex items-center gap-0.5">
                        Detail <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </KartuBaris>
                ))}
              </LoadingState>
            </DaftarKartuMobile>
          </div>
        </>
      )}

      {showForm && <FormCreateCN kategoriOpts={kategoriOpts} cabangOptions={cabangOptions}
                                  onClose={() => { setShowForm(false); bump(); }}
                                  onCreated={(item) => { setShowForm(false); navigate(`/finance/credit-note/${item.id}`); }} />}
      {showKategori && <KelolaKategoriCN onClose={() => { setShowKategori(false); bump(); }} />}
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

// ============ Dashboard KPI ============
// CATATAN: backend TIDAK punya endpoint agregat khusus untuk KPI ini (sudah
// dicek — hanya ada GET /credit-note/aging-summary untuk grafik aging). Kartu
// & tabel per-supplier di bawah dihitung di klien dari respons GET /credit-note
// (tanpa filter, limit tinggi) yang SUDAH membawa field `_aging`/`overdue`/
// `outstanding` terhitung server — jadi tidak ada rumus aging/outstanding yang
// diduplikasi di sini, cuma agregasi (jumlah/rata-rata) dari angka yang backend
// sudah kirim. `rata-rata hari penyelesaian` dihitung dari histori settlement
// yang juga sudah ada di tiap dokumen (`settlements[]`).
function CreditNoteDashboard({ reloadKey, onNavigateFiltered }) {
  const [aging, setAging] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [ag, ls] = await Promise.all([
        api.get(`${CN_BASE}/aging-summary`),
        api.get(CN_BASE, { params: { limit: 2000, sort_by: "created_at", sort_dir: "desc" } }),
      ]);
      setAging(ag.data);
      setList(ls.data);
    } catch (e) {
      setError(e);
      toast.error(e?.response?.data?.detail || "Gagal memuat dashboard Credit Note");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [reloadKey]);

  const kpi = useMemo(() => {
    const items = list?.items || [];
    let totalNilai = 0, totalOutstanding = 0, totalCair = 0, totalTerpakai = 0, overdueCount = 0, overdueTotal = 0;
    const durations = [];
    const bySupplier = new Map();
    for (const it of items) {
      totalNilai += it.nilai || 0;
      totalOutstanding += it.outstanding || 0;
      totalCair += it.nominal_cair || 0;
      totalTerpakai += it.nominal_terpakai || 0;
      if (it.overdue) { overdueCount += 1; overdueTotal += it.outstanding || 0; }
      const selesai = ["cair", "terpakai", "closed"].includes(it.status);
      if (selesai && it.tanggal_diserahkan) {
        const tglSettlement = (it.settlements || [])
          .filter((s) => !s.voided && s.tanggal)
          .map((s) => s.tanggal)
          .sort()
          .slice(-1)[0];
        if (tglSettlement) {
          const hari = Math.round((new Date(tglSettlement) - new Date(it.tanggal_diserahkan)) / 86400000);
          if (hari >= 0) durations.push(hari);
        }
      }
      if (it.outstanding > 0.5) {
        const key = it.supplier_nama || "(Tanpa Nama)";
        const s = bySupplier.get(key) || { supplier: key, count: 0, outstanding: 0, overdue: 0, agingSum: 0, agingN: 0 };
        s.count += 1;
        s.outstanding += it.outstanding || 0;
        if (it.overdue) s.overdue += 1;
        if (it._aging?.hari != null) { s.agingSum += it._aging.hari; s.agingN += 1; }
        bySupplier.set(key, s);
      }
    }
    const avgDays = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null;
    const supplierRows = [...bySupplier.values()]
      .map((s) => ({ ...s, avgAging: s.agingN ? Math.round(s.agingSum / s.agingN) : null }))
      .sort((a, b) => (b.avgAging ?? -1) - (a.avgAging ?? -1))
      .slice(0, 10);
    return { count: items.length, totalNilai, totalOutstanding, totalCair, totalTerpakai, overdueCount, overdueTotal, avgDays, supplierRows };
  }, [list]);

  if (loading) return <LoadingState loading varian="kartu" jumlahKartu={6} data-testid="cn-dash-loading" />;
  if (error) return <LoadingState loading={false} error={error} onRetry={load} varian="kartu" data-testid="cn-dash-error" />;

  const cards = [
    { label: "Total Credit Note", value: idr(kpi.totalNilai), sub: `${kpi.count} dokumen`, icon: FileStack, tone: "text-slate-900" },
    { label: "Outstanding", value: idr(kpi.totalOutstanding), sub: "belum cair/terpakai", icon: Wallet, tone: "text-slate-900" },
    { label: "Sudah Cair", value: idr(kpi.totalCair), sub: "diterima tunai/transfer", icon: CheckCircle2, tone: "text-emerald-700" },
    { label: "Sudah Terpakai", value: idr(kpi.totalTerpakai), sub: "memotong utang usaha", icon: TrendingUp, tone: "text-emerald-700" },
    { label: "Overdue", value: idr(kpi.overdueTotal), sub: `${kpi.overdueCount} CN lewat expected settlement`, icon: AlertTriangle, tone: "text-rose-700" },
    { label: "Rata-rata Hari Penyelesaian", value: kpi.avgDays != null ? `${kpi.avgDays} hari` : "-", sub: "diserahkan → cair/terpakai penuh", icon: RefreshCw, tone: "text-slate-900" },
  ];

  return (
    <div className="space-y-6" data-testid="cn-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`cn-kpi-${c.label}`} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-slate-50 text-slate-700 grid place-items-center"><c.icon className="w-4.5 h-4.5" /></div>
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className={`font-display text-xl font-bold mt-1 ${c.tone}`}>{c.value}</p>
            <p className="text-[11px] text-slate-500 mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="cn-aging-chart">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="font-display font-bold text-base text-slate-900">Aging Outstanding</p>
              <p className="text-xs text-slate-500 mt-0.5">Per {aging?.as_of ? formatTanggal(aging.as_of) : "-"} · Macet (Warning+) {idr(aging?.total_macet || 0)} ({aging?.pct_macet || 0}%)</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={aging?.buckets || []} margin={{ top: 6, right: 6, left: 6, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#64748B" }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
              <Tooltip formatter={(v) => idr(v)} contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#fff", fontSize: 11 }} />
              <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                {(aging?.buckets || []).map((b, i) => <Cell key={i} fill={b.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="cn-supplier-table">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="font-display font-bold text-base text-slate-900">Supplier Paling Lambat</p>
              <p className="text-xs text-slate-500 mt-0.5">Diurutkan dari rata-rata aging tertinggi (yang masih outstanding)</p>
            </div>
          </div>
          {kpi.supplierRows.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Tidak ada outstanding saat ini.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-left py-2 pr-2">Supplier</th>
                    <th className="text-right py-2 px-2">CN</th>
                    <th className="text-right py-2 px-2">Outstanding</th>
                    <th className="text-right py-2 px-2">Overdue</th>
                    <th className="text-right py-2 pl-2">Avg. Aging</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {kpi.supplierRows.map((s) => (
                    <tr key={s.supplier} data-testid={`cn-sup-row-${s.supplier}`} onClick={() => onNavigateFiltered?.({ supplier: s.supplier })}
                        className="hover:bg-slate-50/60 cursor-pointer">
                      <td className="py-2 pr-2 font-medium text-slate-900">{s.supplier}</td>
                      <td className="py-2 px-2 text-right text-slate-700">{s.count}</td>
                      <td className="py-2 px-2 text-right font-mono text-slate-900">{idr(s.outstanding)}</td>
                      <td className="py-2 px-2 text-right">{s.overdue > 0 ? <span className="text-rose-700 font-semibold">{s.overdue}</span> : <span className="text-slate-400">0</span>}</td>
                      <td className="py-2 pl-2 text-right text-slate-700">{s.avgAging != null ? `${s.avgAging}h` : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Form: Credit Note Baru ============
function FormCreateCN({ kategoriOpts, cabangOptions, onClose, onCreated }) {
  const [f, setF] = useState({ ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);
  const valid = f.supplier_nama.trim() && f.kategori && Number(f.nilai) > 0;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const body = {
        supplier_nama: f.supplier_nama.trim(),
        brand: f.brand.trim() || null,
        cabang: f.cabang || null,
        kategori: f.kategori,
        nilai: Number(f.nilai),
        tanggal_terbit: f.tanggal_terbit || undefined,
        no_referensi: f.no_referensi.trim() || null,
        keterangan: f.keterangan || "",
        expected_settlement_date: f.expected_settlement_date || null,
      };
      const r = await api.post(CN_BASE, body);
      toast.success(r.data.message || "Credit Note tersimpan ✓");
      onCreated?.(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan Credit Note"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Credit Note Baru" icon={FileStack} onClose={onClose} onSubmit={submit}
           submitLabel="Simpan sebagai Draft" busy={busy} disabled={!valid} testid="cn-form-modal"
           // BERBAHAYA: 9 field termasuk nilai CN (uang) — bukan sekadar konfirmasi.
           dangerous>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nama Supplier" col2>
          <input value={f.supplier_nama} onChange={(e) => setF({ ...f, supplier_nama: e.target.value })} data-testid="cn-form-supplier" className={inputCls} />
        </Field>
        <Field label="Brand (opsional)">
          <input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} data-testid="cn-form-brand" className={inputCls} />
        </Field>
        <Field label="Cabang (opsional)">
          <select value={f.cabang} onChange={(e) => setF({ ...f, cabang: e.target.value })} data-testid="cn-form-cabang" className={inputCls}>
            {cabangOptions.map((c) => <option key={c || "_none"} value={c}>{c || "-"}</option>)}
          </select>
        </Field>
        <Field label="Kategori">
          <select value={f.kategori} onChange={(e) => setF({ ...f, kategori: e.target.value })} data-testid="cn-form-kategori" className={inputCls}>
            <option value="">Pilih...</option>
            {kategoriOpts.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        <Field label="Nilai CN">
          <InputRupiah value={f.nilai} onChange={(n) => setF({ ...f, nilai: n })} testid="cn-form-nilai" className={inputCls} />
        </Field>
        <Field label="Tanggal Terbit">
          <DatePicker value={f.tanggal_terbit} onChange={(v) => setF({ ...f, tanggal_terbit: v })} data-testid="cn-form-tgl-terbit" />
        </Field>
        <Field label="No. Referensi Supplier (opsional)">
          <input value={f.no_referensi} onChange={(e) => setF({ ...f, no_referensi: e.target.value })} data-testid="cn-form-noref" className={inputCls} />
        </Field>
        <Field label="Expected Settlement (opsional)">
          <DatePicker value={f.expected_settlement_date} onChange={(v) => setF({ ...f, expected_settlement_date: v })} data-testid="cn-form-esd" />
        </Field>
        <Field label="Keterangan" col2>
          <textarea rows={2} value={f.keterangan} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="cn-form-ket" className={inputCls + " h-auto py-2"} />
        </Field>
      </div>
    </Modal>
  );
}

// ============ Kelola Kategori CN ============
function KelolaKategoriCN({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [nama, setNama] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setLoadError(null);
    try { const r = await api.get(`${CN_BASE}/kategori-options`); setItems(r.data.items || []); }
    catch (e) { setLoadError(e); toast.error("Gagal memuat kategori"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const tambah = async () => {
    if (!nama.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`${CN_BASE}/kategori`, { nama: nama.trim() });
      toast.success(r.data.message || "Kategori ditambahkan ✓");
      setNama(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah kategori"); }
    finally { setBusy(false); }
  };

  const nonaktifkan = async (it) => {
    if (it.is_default) return toast.error("Kategori bawaan tidak bisa dinonaktifkan lewat sini");
    if (!window.confirm(`Nonaktifkan kategori '${it.nama}'?`)) return;
    try { const r = await api.delete(`${CN_BASE}/kategori/${it.id}`); toast.success(r.data.message || "Dinonaktifkan ✓"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menonaktifkan"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Tag className="w-5 h-5 text-emerald-600" /> Kelola Kategori CN</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="flex gap-2">
            <input value={nama} onChange={(e) => setNama(e.target.value)} placeholder="Nama kategori baru" data-testid="cn-kat-input"
                   className={inputCls} onKeyDown={(e) => { if (e.key === "Enter") tambah(); }} />
            <button onClick={tambah} disabled={busy || !nama.trim()} data-testid="cn-kat-add"
                    className="h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 shrink-0">Tambah</button>
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <LoadingState loading={loading} error={loadError} onRetry={load} kosong={items.length === 0} pesanKosong="Belum ada kategori" varian="kartu" data-testid="cn-kat-loading">
              <div className="divide-y divide-slate-100">
                {items.map((it) => (
                  <div key={it.id} className="px-3 py-2 flex items-center justify-between text-sm">
                    <span className="text-slate-800">{it.nama}</span>
                    <div className="flex items-center gap-2">
                      {it.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Bawaan</span>}
                      {!it.is_default && (
                        <button onClick={() => nonaktifkan(it)} data-testid={`cn-kat-del-${it.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </LoadingState>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Modal & Field generik (pola sama dengan FinanceReimbursement.jsx) ============
export const inputCls = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

export function Field({ label, hint, children, col2 }) {
  return (
    <div className={col2 ? "col-span-2" : ""}>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

// `dangerous` opsional (default false = tutup langsung, sama seperti
// sebelumnya) — pasang HANYA pada modal yang formnya panjang atau menyentuh
// nominal yang diketik staf sendiri. Lihat docstring useModalGuard.jsx.
export function Modal({ title, icon: Icon, tone, children, onClose, onSubmit, submitLabel, busy, disabled, testid, wide, dangerous }) {
  const guard = useModalGuard({ onClose, onSubmit, busy, disabled, dangerous });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={(e) => e.stopPropagation()} data-testid={testid}
           className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[88vh] flex flex-col shadow-2xl`}>
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            {Icon && <Icon className={`w-5 h-5 ${tone === "rose" ? "text-rose-600" : "text-emerald-600"}`} />} {title}
          </h3>
          <button onClick={guard.requestClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">{children}</div>
        {guard.confirming && (
          <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid={testid ? `${testid}-guard` : "modal-guard-bar"} />
        )}
        {onSubmit && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
            <button onClick={guard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">Batal</button>
            <button onClick={onSubmit} disabled={busy || disabled} data-testid={`${testid}-submit`}
                    className={`h-10 px-4 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${tone === "rose" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
              {busy ? "Memproses..." : submitLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export { CN_BASE };
